import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import type { HTMLAttributes } from "react";
import React from "react";
import { AutocompleteOption } from "../../utils/interfaces";

interface SelectFilterProps {
  value: string | string[] | null;
  onChange: (value: string | string[] | null) => void;
  options?: AutocompleteOption[];
  multiple?: boolean;
  label: string;
}

export const SelectFilter: React.FC<SelectFilterProps> = ({
  value,
  onChange,
  options = [],
  multiple = false,
  label,
}) => {
  const isDisabled = !options || options.length === 0;

  const isOptionEqualToValue = (
    option: AutocompleteOption,
    val: AutocompleteOption,
  ) => option.id === val.id;

  const getOptionLabel = (option: AutocompleteOption) => option.name;

  const getSelectedOptions = () => {
    if (multiple) {
      return options.filter((opt) => (value as string[])?.includes(opt.id));
    }
    return options.find((opt) => opt.id === value) ?? null;
  };

  const handleChange = (
    _: any,
    selected: AutocompleteOption | AutocompleteOption[] | null,
  ) => {
    if (multiple) {
      onChange((selected as AutocompleteOption[]).map((opt) => opt.id));
    } else {
      onChange((selected as AutocompleteOption | null)?.id ?? null);
    }
  };

  const renderOption = (
    props: HTMLAttributes<HTMLLIElement>,
    option: AutocompleteOption,
  ) => {
    const isSelected = multiple
      ? (value as string[])?.includes(option.id)
      : value === option.id;

    return (
      <li
        {...props}
        key={option.id}
        className={`flex items-center gap-2 px-3 py-2 cursor-pointer
          ${
            isSelected
              ? "bg-blue-50 font-semibold hover:bg-blue-100"
              : "hover:bg-gray-100"
          }`}
      >
        {option.icon && <span className='shrink-0'>{option.icon}</span>}
        <span className='truncate'>{option.name}</span>
      </li>
    );
  };

  const selectedOptions = getSelectedOptions();
  const displayValue = multiple
    ? (selectedOptions as AutocompleteOption[])
        .map((opt) => opt.name)
        .join(", ")
    : (selectedOptions as AutocompleteOption)?.name || "";

  const selectedIcon =
    !multiple && (selectedOptions as AutocompleteOption)?.icon;

  return (
    <div className='space-y-1.5 text-black text-base font-medium'>
      <div data-testid='dateFilterLabel'>Select</div>
      <Tooltip
        title={isDisabled ? "Options unavailable or still loading" : ""}
        placement='top'
      >
        <div>
          <Autocomplete
            className='w-80'
            multiple={multiple}
            options={options}
            disableCloseOnSelect={multiple}
            isOptionEqualToValue={isOptionEqualToValue}
            getOptionLabel={getOptionLabel}
            value={selectedOptions}
            onChange={handleChange}
            size='small'
            renderTags={() => null}
            renderOption={renderOption}
            disabled={isDisabled}
            renderInput={(params) => {
              const inputValue = displayValue;

              return (
                <TextField
                  {...params}
                  placeholder={!inputValue ? label : ""}
                  variant='outlined'
                  inputProps={{
                    ...params.inputProps,
                    value: inputValue,
                  }}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: selectedIcon ? (
                      <span className='ml-2 mr-1 flex items-center'>
                        {selectedIcon}
                      </span>
                    ) : undefined,
                  }}
                />
              );
            }}
            data-testid='selectFilter'
          />
        </div>
      </Tooltip>
    </div>
  );
};
